import { Children } from 'react'

/**
 * FlowShell — Perspective-style dark full-screen layout used for
 * signup and onboarding steps. Renders:
 *  - A 3 px green progress bar pinned to the very top
 *  - A centred 390 px content column
 *  - A fixed bottom CTA slot (children[1] if provided)
 *
 * Usage:
 *   <FlowShell step={step} totalSteps={3}>
 *     <StepContent />            ← body
 *     <PrimaryButton />          ← bottom CTA (optional)
 *   </FlowShell>
 */
export default function FlowShell({ step, totalSteps, children }) {
  const pct = totalSteps > 1 ? Math.round((step / (totalSteps - 1)) * 100) : 0
  // React.Children.toArray strips false/null/undefined so conditional children
  // (e.g. {step === 1 && <OrgStep />} evaluating to false) don't shift indices.
  const childArray = Children.toArray(children)
  const [body, cta] = childArray

  return (
    <div className="min-h-screen bg-[#0F0F0F] relative">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-white/10">
        <div
          className="h-full bg-green-500 transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Scrollable body */}
      <div className="min-h-screen pt-14 pb-32 px-6 overflow-y-auto">
        <div className="max-w-[390px] mx-auto w-full">
          {/* Re-key on step so React unmounts → triggers step-enter animation */}
          <div key={step} className="animate-step-enter">
            {body}
          </div>
        </div>
      </div>

      {/* Fixed bottom CTA */}
      {cta && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 px-6 pb-6"
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          <div className="max-w-[390px] mx-auto w-full">
            {cta}
          </div>
        </div>
      )}
    </div>
  )
}
